/**
 * ChatHeader - 聊天页面头部组件
 * 支持面包屑导航：
 * - 项目下的 conversation: 项目名 / conversation标题
 * - 分享下的 conversation: Shared / conversation标题
 * - 普通 conversation: 只显示标题（无面包屑）
 */

import { ChevronRight } from 'lucide-react';
import { useSkillTaskStore } from '@/stores/skillTaskStore';

// 🔥 角色颜色配置（带边框，与其他地方保持一致）
const ROLE_CONFIG: Record<
  string,
  { label: string; color: string; borderColor: string }
> = {
  owner: {
    label: 'Owner',
    color: 'bg-purple-100 text-purple-700',
    borderColor: 'border-purple-200',
  },
  manager: {
    label: 'Manager',
    color: 'bg-blue-100 text-blue-700',
    borderColor: 'border-blue-200',
  },
  editor: {
    label: 'Editor',
    color: 'bg-green-100 text-green-700',
    borderColor: 'border-green-200',
  },
  viewer: {
    label: 'Viewer',
    color: 'bg-gray-100 text-gray-600',
    borderColor: 'border-gray-200',
  },
};

interface ChatHeaderProps {
  title?: string;
  // 项目相关
  projectId?: string | null;
  projectName?: string | null;
  projectRole?: string | null; // 🔥 新增：项目角色
  onProjectClick?: (projectId: string) => void;
  // 分享相关
  isSharedRecordView?: boolean;
  onBackToShared?: () => void;
  // Skill 相关
  isSkillConversation?: boolean;
  conversationId?: string; // 当前对话 ID，用于过滤 Tasks Status 数据
}

export function ChatHeader({
  title = 'New Chat',
  projectId,
  projectName,
  projectRole,
  onProjectClick,
  isSharedRecordView,
  onBackToShared,
  isSkillConversation = false,
  conversationId,
}: ChatHeaderProps) {
  // 判断是否显示面包屑
  const showProjectBreadcrumb = projectId && projectName;
  const showSharedBreadcrumb = isSharedRecordView && !projectId;
  // 获取角色配置
  const roleConfig = projectRole ? ROLE_CONFIG[projectRole] : null;

  // 🔥 Skill Task Dashboard
  const { boards, isDashboardOpen, setDashboardOpen } = useSkillTaskStore();
  // 🔥 只计算当前对话的运行中 board 数量，而非全局
  const currentBoard = conversationId ? boards[conversationId] : undefined;
  const runningCount = currentBoard
    ? currentBoard.steps.some(s => s.startedAt && !s.finishedAt && !s.error)
      ? 1
      : 0
    : 0;

  return (
    <header className="flex-shrink-0 bg-white pr-2 pl-0 py-1.5 overflow-hidden lg:pl-4">
      {/* 移动端无面包屑时，标题需要额外左侧 padding 避开汉堡按钮 */}
      <div className="flex items-center justify-between min-w-0 max-w-full">
        <nav className="flex items-center gap-1 min-w-0 flex-1">
          {/* 项目面包屑：项目名 / 标题 */}
          {showProjectBreadcrumb && (
            <>
              <button
                type="button"
                onClick={() => onProjectClick?.(projectId)}
                className="text-sm text-gray-900 hover:text-gray-600 hover:underline transition-colors font-semibold max-w-[300px] truncate"
                title={`Go to project: ${projectName}`}
              >
                {projectName}
              </button>
              {/* 🔥 角色标签（带边框） */}
              {roleConfig && (
                <span
                  className={`inline-flex items-center px-2 py-0.5 ml-2 text-xs font-medium rounded-full border ${roleConfig.color} ${roleConfig.borderColor}`}
                >
                  {roleConfig.label}
                </span>
              )}
              <ChevronRight
                size={14}
                className="text-gray-400 flex-shrink-0 mx-0.5"
              />
            </>
          )}

          {/* 分享面包屑：Shared / 标题 */}
          {showSharedBreadcrumb && (
            <>
              <button
                type="button"
                onClick={onBackToShared}
                className="text-sm text-gray-900 hover:text-gray-600 hover:underline transition-colors font-semibold max-w-[600px] truncate"
                title="Back to Shared"
              >
                Shared
              </button>
              <ChevronRight
                size={14}
                className="text-gray-400 flex-shrink-0 mx-0.5"
              />
            </>
          )}

          {/* 当前页面标题 */}
          <h4
            title={title}
            className="text-sm font-medium text-gray-600 max-w-[600px] truncate"
          >
            {title}
          </h4>
        </nav>

        {/* 🔥 数据看板按钮（右上角）- 只在 skill 对话时显示 */}
        {isSkillConversation && (
          <div className="flex items-center gap-2 flex-shrink-0 mr-10">
            <button
              data-dashboard-trigger
              type="button"
              onClick={() => setDashboardOpen(!isDashboardOpen)}
              className="relative p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Skill Tasks Dashboard"
            >
              <img
                src="/static/task_progress.svg"
                alt="Task Progress"
                className="w-[18px] h-[18px]"
              />
              {/* 红点徽标 - 直接显示 running 中的任务数量 */}
              {runningCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none shadow-sm">
                  {runningCount}
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default ChatHeader;
